/**
 * Menu — the popover surface that lists options when a trigger opens (src/ui/ds).
 *
 * The organism of the menu family: a scrollable, elevated surface that composes
 * MenuGroups / MenuItems and owns keyboard navigation + focus. Pair it with a
 * DropdownSelector (or any trigger) inside a Popover; this component renders the
 * surface, the trigger renders the button.
 *
 * NOTE (portal port): upstream's `searchable` header is NOT ported — it depends
 * on the main app's SearchBox, whose API differs here. Port it alongside that
 * component if a portal menu ever needs search.
 *
 * Keyboard (hand-rolled, since the family is pure-custom, not Chakra Menu):
 *   ↑/↓ move focus between enabled rows (wrapping), Home/End jump to ends,
 *   Esc calls onClose, type a letter to jump to the next row starting with it.
 *   Enter/Space activate the focused row natively (it's a button). When the
 *   search field is focused, typing filters and ↑/↓ steps into the list.
 * `autoFocus` focuses the search field (if searchable) or the first row on mount
 *   — runtime opens want this; the gallery omits it so it doesn't steal focus.
 *
 * Token-driven surface: bg.container, component.2xl radius, elevHigh shadow
 * (no border — the strong elevation defines the edge), 1xs padding, capped
 * height with scroll.
 *
 *
 * PORTED from ploy-client src/ui/ds/Menu.tsx — the canonical design-system
 * component. Do not restyle here; upstream owns it. Portal-local changes:
 * dsTag inlined, viewer-only `whenToUse` dropped.
 *
 * Location: src/components/ds/Menu.tsx
 */
import { chakra } from "@chakra-ui/react";
import { useEffect, useRef } from "react";
import type { ReactNode } from "react";

/* dev-only DOM marker (inlined from ploy-client's ds/dsTag) — compiles away
   in production builds. */
const dsTag = (name: string): Record<string, string> =>
  import.meta.env.DEV ? { "data-ds": name } : {};

export interface MenuProps {
  /** MenuGroups and/or MenuItems */
  children: ReactNode;
  /** accessible name for the menu (e.g. the trigger's label) */
  ariaLabel?: string;
  /** max height before the list scrolls */
  maxH?: string;
  /** focus the search field / first row on mount — runtime opens want this */
  autoFocus?: boolean;
  /** called on Escape */
  onClose?: () => void;
}

export function Menu({
  children,
  ariaLabel,
  maxH = "320px",
  autoFocus = false,
  onClose,
}: MenuProps) {
  const ref = useRef<HTMLDivElement>(null);

  const items = (): HTMLButtonElement[] =>
    Array.from(ref.current?.querySelectorAll<HTMLButtonElement>('button[role^="menuitem"]:not([disabled])') ?? []);

  useEffect(() => {
    if (!autoFocus) return;
    items()[0]?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoFocus]);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      onClose?.();
      return;
    }
    const its = items();
    if (!its.length) return;
    const cur = its.indexOf(document.activeElement as HTMLButtonElement);
    const inSearch = false;
    const focusAt = (i: number) => {
      e.preventDefault();
      its[(i + its.length) % its.length].focus();
    };
    switch (e.key) {
      case "ArrowDown":
        focusAt(cur < 0 ? 0 : cur + 1);
        break;
      case "ArrowUp":
        focusAt(cur < 0 ? its.length - 1 : cur - 1);
        break;
      case "Home":
        if (!inSearch) focusAt(0); // in the search field, Home moves the text cursor
        break;
      case "End":
        if (!inSearch) focusAt(its.length - 1);
        break;
      default:
        // type-ahead jump — only when a row is focused, not while typing a query
        if (!inSearch && e.key.length === 1 && e.key.trim()) {
          const lower = e.key.toLowerCase();
          for (let k = 1; k <= its.length; k++) {
            const el = its[(cur + k) % its.length];
            if (el.textContent?.trim().toLowerCase().startsWith(lower)) {
              e.preventDefault();
              el.focus();
              break;
            }
          }
        }
    }
  };

  return (
    <chakra.div
      {...dsTag("Menu")}
      ref={ref}
      role="menu"
      aria-label={ariaLabel}
      onKeyDown={onKeyDown}
      display="flex"
      flexDirection="column"
      minW="200px"
      maxH={maxH}
      overflow="hidden"
      bg="bg.container"
      borderRadius="component.2xl"
      // Portal theme spells this token `elevHigh` (upstream: `elev-high`).
      boxShadow="elevHigh"
    >
      <chakra.div display="flex" flexDirection="column" flex="1 1 auto" overflowY="auto" p="1xs">
        {children}
      </chakra.div>
    </chakra.div>
  );
}

export default Menu;
